import React, { useMemo } from 'react';
import { Box, keyframes } from '@mui/material';
import type { ThemeData, ThemeDecoration } from '../../types';

// Security: Validate URLs to prevent XSS via javascript: or data: URLs
const isValidImageUrl = (url: string | undefined): boolean => {
  if (!url) return false;
  try {
    const parsed = new URL(url, window.location.origin);
    // Only allow http/https protocols
    return parsed.protocol === 'http:' || parsed.protocol === 'https:';
  } catch {
    // If URL parsing fails, check for relative URLs starting with /
    return url.startsWith('/') && !url.startsWith('//');
  }
};

// Security: Sanitize decoration content to prevent XSS
// Only allow emoji-like content (short strings without HTML)
const isSafeDecorationContent = (content: string | undefined): boolean => {
  if (!content) return false;
  // Max length check and no HTML-like characters
  return content.length <= 10 && !/[<>]/.test(content);
};

// Animation keyframes
const snowfall = keyframes`
  0% {
    transform: translateY(-10vh) translateX(0);
    opacity: 1;
  }
  100% {
    transform: translateY(100vh) translateX(20px);
    opacity: 0.3;
  }
`;

const fireworkBurst = keyframes`
  0% {
    transform: scale(0);
    opacity: 1;
  }
  50% {
    transform: scale(1.2);
    opacity: 0.8;
  }
  100% {
    transform: scale(1);
    opacity: 0.6;
  }
`;

const confettiFall = keyframes`
  0% {
    transform: translateY(-10vh) rotate(0deg);
    opacity: 1;
  }
  100% {
    transform: translateY(100vh) rotate(720deg);
    opacity: 0;
  }
`;

interface ThemeWrapperProps {
  themeData?: ThemeData;
  children: React.ReactNode;
  showAnimations?: boolean; // For live view, not export
  className?: string;
}

// Helper to get position styles from decoration position
const getPositionStyles = (
  position: ThemeDecoration['position'],
  offsetX?: string,
  offsetY?: string
): React.CSSProperties => {
  const baseStyles: React.CSSProperties = { position: 'absolute' };

  switch (position) {
    case 'top-left':
      return { ...baseStyles, top: offsetY || '0', left: offsetX || '0' };
    case 'top-right':
      return { ...baseStyles, top: offsetY || '0', right: offsetX?.replace('-', '') || '0' };
    case 'bottom-left':
      return { ...baseStyles, bottom: offsetY?.replace('-', '') || '0', left: offsetX || '0' };
    case 'bottom-right':
      return { ...baseStyles, bottom: offsetY?.replace('-', '') || '0', right: offsetX?.replace('-', '') || '0' };
    case 'top':
      return { ...baseStyles, top: offsetY || '0', left: '50%', transform: 'translateX(-50%)' };
    case 'bottom':
      return { ...baseStyles, bottom: offsetY?.replace('-', '') || '0', left: '50%', transform: 'translateX(-50%)' };
    case 'left':
      return { ...baseStyles, left: offsetX || '0', top: '50%', transform: 'translateY(-50%)' };
    case 'right':
      return { ...baseStyles, right: offsetX?.replace('-', '') || '0', top: '50%', transform: 'translateY(-50%)' };
    default:
      return baseStyles;
  }
};

// Render a single decoration
const Decoration: React.FC<{ decoration: ThemeDecoration }> = ({ decoration }) => {
  const positionStyles = getPositionStyles(decoration.position, decoration.offsetX, decoration.offsetY);

  if (decoration.type === 'emoji') {
    // Security: Only render safe emoji content
    if (!isSafeDecorationContent(decoration.content)) {
      return null;
    }
    return (
      <Box
        sx={{
          ...positionStyles,
          fontSize: decoration.size || '40px',
          opacity: decoration.opacity ?? 1,
          pointerEvents: 'none',
          userSelect: 'none',
          zIndex: 1,
        }}
      >
        {decoration.content}
      </Box>
    );
  }

  if (decoration.type === 'image') {
    // Security: Only render valid image URLs (http/https only)
    if (!isValidImageUrl(decoration.content)) {
      return null;
    }
    return (
      <Box
        component="img"
        src={decoration.content}
        alt=""
        sx={{
          ...positionStyles,
          width: decoration.size || '40px',
          height: 'auto',
          opacity: decoration.opacity ?? 1,
          pointerEvents: 'none',
          zIndex: 1,
        }}
      />
    );
  }

  return null;
};

// Snow animation component
const SnowAnimation: React.FC = () => {
  const snowflakes = useMemo(() => {
    return Array.from({ length: 30 }, (_, i) => ({
      id: i,
      left: `${Math.random() * 100}%`,
      delay: `${Math.random() * 5}s`,
      duration: `${3 + Math.random() * 4}s`,
      size: `${8 + Math.random() * 8}px`,
    }));
  }, []);

  return (
    <Box
      sx={{
        position: 'absolute',
        inset: 0,
        overflow: 'hidden',
        pointerEvents: 'none',
        zIndex: 2,
      }}
    >
      {snowflakes.map((flake) => (
        <Box
          key={flake.id}
          sx={{
            position: 'absolute',
            left: flake.left,
            top: '-20px',
            fontSize: flake.size,
            animation: `${snowfall} ${flake.duration} ease-in-out infinite`,
            animationDelay: flake.delay,
            opacity: 0.8,
          }}
        >
          ❄
        </Box>
      ))}
    </Box>
  );
};

// Fireworks animation component
const FireworksAnimation: React.FC = () => {
  const bursts = useMemo(() => {
    return Array.from({ length: 8 }, (_, i) => ({
      id: i,
      left: `${10 + Math.random() * 80}%`,
      top: `${10 + Math.random() * 40}%`,
      delay: `${Math.random() * 3}s`,
      duration: `${2 + Math.random() * 2}s`,
    }));
  }, []);

  return (
    <Box
      sx={{
        position: 'absolute',
        inset: 0,
        overflow: 'hidden',
        pointerEvents: 'none',
        zIndex: 2,
      }}
    >
      {bursts.map((burst) => (
        <Box
          key={burst.id}
          sx={{
            position: 'absolute',
            left: burst.left,
            top: burst.top,
            fontSize: '24px',
            animation: `${fireworkBurst} ${burst.duration} ease-out infinite`,
            animationDelay: burst.delay,
          }}
        >
          ✨
        </Box>
      ))}
    </Box>
  );
};

// Confetti animation component
const ConfettiAnimation: React.FC = () => {
  const confetti = useMemo(() => {
    const colors = ['🎊', '🎉', '✨', '⭐'];
    return Array.from({ length: 20 }, (_, i) => ({
      id: i,
      left: `${Math.random() * 100}%`,
      delay: `${Math.random() * 4}s`,
      duration: `${4 + Math.random() * 3}s`,
      content: colors[i % colors.length],
    }));
  }, []);

  return (
    <Box
      sx={{
        position: 'absolute',
        inset: 0,
        overflow: 'hidden',
        pointerEvents: 'none',
        zIndex: 2,
      }}
    >
      {confetti.map((piece) => (
        <Box
          key={piece.id}
          sx={{
            position: 'absolute',
            left: piece.left,
            top: '-20px',
            fontSize: '16px',
            animation: `${confettiFall} ${piece.duration} linear infinite`,
            animationDelay: piece.delay,
          }}
        >
          {piece.content}
        </Box>
      ))}
    </Box>
  );
};

export const ThemeWrapper: React.FC<ThemeWrapperProps> = ({
  themeData,
  children,
  showAnimations = true,
  className,
}) => {
  if (!themeData) {
    return <>{children}</>;
  }

  const hasPageStyling = themeData.pageBackground || themeData.pageGradient || themeData.backgroundImage;
  const hasDecorations = themeData.decorations && themeData.decorations.length > 0;
  const hasAnimation = showAnimations && themeData.animation && themeData.animation !== 'none';

  // If no visual enhancements, just render children
  if (!hasPageStyling && !hasDecorations && !hasAnimation) {
    return <>{children}</>;
  }

  // Security: Validate background image URL
  const safeBackgroundImage = isValidImageUrl(themeData.backgroundImage)
    ? `url(${themeData.backgroundImage})`
    : undefined;

  return (
    <Box
      className={className}
      sx={{
        position: 'relative',
        minHeight: '100%',
        background: themeData.pageGradient || themeData.pageBackground || 'transparent',
        backgroundImage: safeBackgroundImage,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        borderRadius: 2,
        p: 2,
        overflow: 'hidden',
      }}
    >
      {/* Background overlay */}
      {themeData.backgroundOverlay && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            backgroundColor: themeData.backgroundOverlay,
            zIndex: 0,
          }}
        />
      )}

      {/* Decorations */}
      {hasDecorations &&
        themeData.decorations!.map((decoration, index) => (
          <Decoration key={index} decoration={decoration} />
        ))}

      {/* Animations */}
      {hasAnimation && themeData.animation === 'snow' && <SnowAnimation />}
      {hasAnimation && themeData.animation === 'fireworks' && <FireworksAnimation />}
      {hasAnimation && themeData.animation === 'confetti' && <ConfettiAnimation />}

      {/* Main content */}
      <Box sx={{ position: 'relative', zIndex: 1 }}>{children}</Box>
    </Box>
  );
};

// Static version for exports (no animations)
export const ThemeWrapperStatic: React.FC<Omit<ThemeWrapperProps, 'showAnimations'>> = (props) => {
  return <ThemeWrapper {...props} showAnimations={false} />;
};

export default ThemeWrapper;
